import React, { useEffect, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { Paper } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box, Button, styled } from '@mui/material';
import Add from '@mui/icons-material/Add';
import QuestionsList from '../../components/questions/QuestionsList';
import {
	dynamicId,
	getQuestionnairePayload,
	reorder,
} from '../../utils/Helpers';
import { CreateQuestionnaireQuestionsProps } from '../../types/CommonTypes';
import { STEPTheme } from '../../utils/Theme';
import QuestionPopup from '../../components/questions/QuestionPopup';
import QuestionSubHeader from '../../components/questions/QuestionSubHeader';
import QuestionHeader from '../../components/questions/QuestionHeader';
import { useAppDispatch, useAppSelector } from '../../app/Hooks';
import {
	APILoadingStatus,
	configurationData,
	resetAPIFlag,
	surveyQuestionnaireData,
	updateQuestionnaireData,
	updateSurveyQuestionnaire,
} from '../../redux/SurveySlice';
import { ISurveyFormsQuestion } from '../../types/SurveyProps';
import { QUE_JOURNEY_STEPS } from '../../utils/Enum';
import GenericModalPopup from '../../components/common/GenericModalPopup';
import { commonLabels } from '../../utils/CommonLabels';
import Loader from '../../components/Loader';
import {
	API_STATUS_CONSTANT,
	QUECONSTANT,
} from '../../constants/StepConstants';
import QuestionnaireFooter from '../../components/questions/QuestionnaireFooter';
import {
	setCheckboxState,
	setDropDownOptionsCount,
	setPageIndex,
} from '../../redux/QuestionnaireSurveySlice';
import { Numbers } from '../../utils/Enum';

const useStyles = makeStyles({
	actionBox: {
		display: 'flex',
		justifyContent: 'end',
		marginTop: '20px',
	},
	plusIcon: {
		width: '0.8em',
	},
	flexPaper: {
		flex: 1,
		margin: 16,
		minWidth: 350,
		maxWidth: '1024px',
		boxShadow: 'none',
		background: STEPTheme.colors.background,
	},
	root: {
		display: 'flex',
		flexWrap: 'wrap',
		background: STEPTheme.colors.background,
		justifyContent: 'center',
	},
});

const AddQuestionButton = styled(Button)({
	fontFamily: "'JohnsonText-Regular'",
	fontSize: '14px',
	fontWeight: '400',
	lineHeight: '16px',
	width: 'auto',
	height: '32px',
	marginBottom: '5px',
	color: STEPTheme.colors.text,
	textTransform: 'capitalize',
	background: 'white',
	padding: '8px 8px 8px 4px',
	borderRadius: '8px',
	border: `1px solid ${STEPTheme.colors.text}`,
	'&:hover': {
		backgroundColor: STEPTheme.colors.white,
		borderColor: STEPTheme.colors.text,
		color: STEPTheme.colors.text,
	},
});

const CreateSurveyQuestions: React.FC<CreateQuestionnaireQuestionsProps> = ({
	handleNext,
	handleBack,
	backToDetailsPage,
	activeStep,
}) => {
	const dispatch = useAppDispatch();
	const classes = useStyles();
	const itemsFack: ISurveyFormsQuestion[] = [];
	const [items, setItems] = React.useState(itemsFack);
	const [popupOpen, setPopupOpen] = React.useState(false);
	const surveyQuestionnaireInfo = useAppSelector(surveyQuestionnaireData);
	const [disableSubmit, setDisableSubmit] = useState<boolean>(true);
	const [open, setOpen] = React.useState(false);
	const handleClosePopup = () => {
		setOpen(false);
		dispatch(resetAPIFlag('idle'));
	};
	const pageFooterIndex = useAppSelector((state) => state.Questionnaire.index);
	const [showLoader, setLoaderOpen] = React.useState(false);
	const [isSaveProceed, setSaveProceed] = React.useState(false);
	const APICompletionStatus = useAppSelector(APILoadingStatus);
	const configs = useAppSelector(configurationData);
	useEffect(() => {
		if (APICompletionStatus === API_STATUS_CONSTANT.SUCCESS) {
			setLoaderOpen(false);
			if (isSaveProceed) {
				dispatch(resetAPIFlag('idle'));
				handleNext();
			} else {
				setOpen(true);
			}
		} else {
			dispatch(resetAPIFlag('idle'));
			setOpen(false);
			setLoaderOpen(false);
			setSaveProceed(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [APICompletionStatus]);

	useEffect(() => {
		// Check if there are questions and questionIsValid is true for all questions
		if (
			surveyQuestionnaireInfo.surveyFormsQuestion.length === 0 ||
			items.length === Numbers.zero
		) {
			setDisableSubmit(true);
		} else if (
			surveyQuestionnaireInfo.surveyFormsQuestion.length > 0 &&
			items.length === surveyQuestionnaireInfo.surveyFormsQuestion.length
		) {
			const invalidQuestionLength =
				surveyQuestionnaireInfo.surveyFormsQuestion.filter((question) => {
					return (
						question.questionIsValid === false ||
						question.questionButtonLabel === commonLabels.save
					);
				}).length;
			if (invalidQuestionLength > 0) {
				setDisableSubmit(true);
			} else {
				setDisableSubmit(false);
			}
		} else {
			if (items.length > surveyQuestionnaireInfo.surveyFormsQuestion.length) {
				const unsavedQuestionLength = items.filter((question) => {
					return question.questionButtonLabel === commonLabels.save;
				}).length;

				if (unsavedQuestionLength > 0) {
					setDisableSubmit(true);
				}
			}
		}
	}, [surveyQuestionnaireInfo, items, disableSubmit]);

	const handleClosePopupQue = (e: string) => {
		setPopupOpen(false);
		if (e !== 'close') {
			addQuestion(e);
		}
	};
	useEffect(() => {
		setItems(surveyQuestionnaireInfo.surveyFormsQuestion);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	const saveAsProceed = () => {
		setSaveProceed(true);
		setLoaderOpen(true);
		const payload = getQuestionnairePayload({
			...surveyQuestionnaireInfo,
			currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
			userEmailId: configs?.user?.userEmailId || '',
		});
		dispatch(updateQuestionnaireData(payload));
	};

	const saveAsDraft = () => {
		setLoaderOpen(true);
		const payload = getQuestionnairePayload({
			...surveyQuestionnaireInfo,
			currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
			userEmailId: configs?.user?.userEmailId || '',
		});
		dispatch(updateQuestionnaireData(payload));
	};

	/**Sync Question name in every input change */
	const onQueInputChange = (
		inputValue: string,
		instructions: string | undefined,
		questionId: string,
		questionObj?: ISurveyFormsQuestion
	) => {
		let updatedItems = [...items];
		updatedItems = updatedItems.map((item) => {
			if (item.questionId === questionId) {
				return {
					...item,
					question: inputValue,
					instructions: instructions,
					isNew: true,
					options: questionObj ? questionObj.options : item.options,
					fileName: questionObj ? questionObj.fileName : '',
				};
			} else {
				return { ...item };
			}
		});
		setItems(updatedItems);
	};

	const onInstructionChange = (
		inputValue: string | undefined,
		questionId: string
	) => {
		let updatedItems = [...items];
		updatedItems = updatedItems.map((item) => {
			if (item.questionId === questionId) {
				return {
					...item,
					instructions: inputValue,
				};
			} else {
				return { ...item };
			}
		});
		setItems(updatedItems);
	};

	const onDragEnd = ({ destination, source }: DropResult) => {
		const updatedData = surveyQuestionnaireInfo.surveyFormsQuestion;
		if (
			items[source.index].isNew ||
			(destination && items[destination.index].isNew)
		) {
			alert(commonLabels.queReorderError);
			return;
		}
		// dropped outside the list
		if (!destination) return;
		const newItems = reorder(updatedData, source.index, destination.index);
		const updatedItems = newItems.map((item, index) => {
			return {
				...item,
				questionId: (Numbers.oneLakhOne + index).toString(),
				order: index,
			};
		});
		dispatch(
			updateSurveyQuestionnaire({
				...surveyQuestionnaireInfo,
				currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
				surveyFormsQuestion: [...updatedItems],
			})
		);
		setItems(updatedItems);
	};

	const addQuestion = (type: string) => {
		const newItem: ISurveyFormsQuestion = {
			questionId: dynamicId(items),
			questionType: type,
			options: [],
			question: '',
			instructions: '',
			isNew: true,
			order: items.length,
			questionIsValid: false,
			questionButtonLabel: commonLabels.save,
		};
		dispatch(
			setCheckboxState({ questionId: newItem.questionId, value: false })
		);
		setDisableSubmit(true);
		setItems((prevItems) => [...prevItems, newItem]);
		if (
			[
				QUECONSTANT.QUESTION_TYPE_YES_NO,
				QUECONSTANT.QUESTION_TYPE_MULTI_SELECT,
				QUECONSTANT.QUESTION_TYPE_OPEN,
				QUECONSTANT.QUESTION_TYPE_MULTIPLE_OPTION,
				QUECONSTANT.QUESTION_TYPE_DROPDOWN,
			].includes(type)
		) {
			const newIndex = pageFooterIndex + 1;
			dispatch(setPageIndex(newIndex));
		}
		if (type === QUECONSTANT.QUESTION_TYPE_DROPDOWN) {
			dispatch(
				setDropDownOptionsCount({
					questionId: newItem.questionId,
					length: 0,
				})
			);
		}
	};

	const handleAddQuestion = () => {
		setPopupOpen(true);
	};
	/**
	 * @description Delete question from local state.
	 * @param deletedQuestionId
	 * @returns items updated
	 */
	const deleteQuesItem = (deletedQuestionId: string) => {
		const updatedItems = [...items];
		const indx = updatedItems.findIndex(
			(item) => item.questionId === deletedQuestionId
		);
		if (indx > Numbers.minusOne) {
			updatedItems.splice(indx, 1);
		}
		return updatedItems;
	};
	/**
	 *
	 * @param updatedItems
	 * @returns synced items from local & global state.
	 */
	const syncAllQuestionState = (
		updatedItems: ISurveyFormsQuestion[],
		surveyFormsQuestion: ISurveyFormsQuestion[]
	) => {
		let syncItems = updatedItems.map((localQuestionState) => {
			const data = surveyFormsQuestion.find(
				(reduxQuestionState) =>
					!localQuestionState.isNew &&
					reduxQuestionState.questionId === localQuestionState.questionId
			);
			if (data) {
				return {
					...localQuestionState,
					...data,
				};
			} else {
				return {
					...localQuestionState,
				};
			}
		});
		syncItems = syncItems.map((data, position) => {
			return {
				...data,
				questionId: (Numbers.oneLakhOne + position).toString(),
				order: position,
			};
		});
		setItems(syncItems);
		return syncItems;
	};

	/**
	 * @description update the option dependecy when question added
	 * @param question
	 * @param deletedQuesOrder
	 * @param index
	 * @returns options Array
	 */
	const quesOptionUpdation = (
		question: ISurveyFormsQuestion,
		deletedQuesOrder: number | undefined,
		index: string
	) => {
		if (question.questionId !== index) {
			return question.options.map((optionItem) => {
				const quesOption = JSON.parse(JSON.stringify(optionItem));
				const selectedQuesOrder =
					optionItem.condition &&
					optionItem.condition.includes(commonLabels.continueTo)
						? optionItem.condition.split(commonLabels.continueTo)[Numbers.one]
						: Numbers.zero;

				quesOption.condition =
					Number(deletedQuesOrder) + Numbers.one === Number(selectedQuesOrder)
						? ''
						: optionItem.condition;
				return {
					...quesOption,
				};
			});
		} else {
			return question.options;
		}
	};

	const handlerDeleteQuestion = (index: string) => {
		const savedSurveyQuestion =
			surveyQuestionnaireInfo.surveyFormsQuestion.find(
				(question) => question.questionId === index
			);
		const updatedItems = deleteQuesItem(index);
		if (savedSurveyQuestion) {
			const deletedQuesOrder = savedSurveyQuestion.order;
			/** Iterate and update the dependency condition option of question then filter. */
			const updatedData: ISurveyFormsQuestion[] =
				surveyQuestionnaireInfo.surveyFormsQuestion
					.map((question) => {
						const updatedOptions = quesOptionUpdation(
							question,
							deletedQuesOrder,
							index
						);
						return {
							...question,
							options: updatedOptions,
						};
					})
					.filter((question) => question.questionId !== index);

			const syncedItems = syncAllQuestionState(updatedItems, updatedData);
			const savedItems = syncedItems.filter((item) => !item.isNew);
			dispatch(
				updateSurveyQuestionnaire({
					...surveyQuestionnaireInfo,
					currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
					surveyFormsQuestion: savedItems,
				})
			);
		} else {
			const syncedItems = syncAllQuestionState(
				updatedItems,
				surveyQuestionnaireInfo.surveyFormsQuestion
			);
			dispatch(
				updateSurveyQuestionnaire({
					...surveyQuestionnaireInfo,
					currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
					surveyFormsQuestion: syncedItems,
				})
			);
		}
		const newIndex = pageFooterIndex - 1;
		dispatch(setPageIndex(newIndex));
	};

	const getOptions = (data: ISurveyFormsQuestion) => {
		const surveyFormsQuestion =
			surveyQuestionnaireInfo.surveyFormsQuestion.find(
				(question) => question.questionId === data.questionId
			);
		if (surveyFormsQuestion) {
			return surveyFormsQuestion.options;
		} else {
			return [...data.options];
		}
	};

	const dropDownTypeQuesLogic = (data: ISurveyFormsQuestion) => {
		const updatedItems = JSON.parse(JSON.stringify(items));
		const indx = updatedItems.findIndex(
			(item: ISurveyFormsQuestion) => item.questionId === data.questionId
		);
		updatedItems[indx].fileName = data?.fileName || '';
		updatedItems[indx].options = getOptions(data);
		setItems(updatedItems);
	};

	const handleQueClone = (data: ISurveyFormsQuestion) => {
		let options = data.options;
		if (data.questionType === QUECONSTANT.QUESTION_TYPE_DROPDOWN) {
			dropDownTypeQuesLogic(data);
		} else {
			data.options = data.options.map((option) => {
				return { ...option, condition: '' };
			});
			options = [...data.options];
		}
		let newItem: ISurveyFormsQuestion = {
			questionId: (Numbers.oneLakhOne + items.length).toString(),
			questionType: data.questionType,
			options: options,
			question: data.question,
			instructions: data.instructions,
			isNew: true,
			order: items.length,
			questionIsValid: false,
			questionButtonLabel: commonLabels.save,
		};
		if (
			data.questionType === QUECONSTANT.QUESTION_TYPE_DROPDOWN &&
			data?.fileName
		) {
			newItem = { ...newItem, fileName: data.fileName };
		}
		if (data.instructions) {
			// disptach to set checked for cloned question
			dispatch(
				setCheckboxState({ questionId: newItem.questionId, value: true })
			);
		}
		setItems((prevItems) => [...prevItems, newItem]);
		if (
			[
				QUECONSTANT.QUESTION_TYPE_YES_NO,
				QUECONSTANT.QUESTION_TYPE_MULTI_SELECT,
				QUECONSTANT.QUESTION_TYPE_OPEN,
				QUECONSTANT.QUESTION_TYPE_MULTIPLE_OPTION,
				QUECONSTANT.QUESTION_TYPE_DROPDOWN,
			].includes(data.questionType)
		) {
			const newIndex = pageFooterIndex + 1;
			dispatch(setPageIndex(newIndex));
		}
	};

	/**Update Ques State items locally */
	const updatLocalStateItmes = (formData: ISurveyFormsQuestion) => {
		let updatedItems = [...items];
		updatedItems = updatedItems.map((item) => {
			if (item.questionId === formData.questionId) {
				return {
					...item,
					...formData,
				};
			} else {
				return { ...item };
			}
		});
		setItems(updatedItems);
	};

	const saveAndEditQuestion = (formData: ISurveyFormsQuestion) => {
		if (formData.isNew) {
			const savedItems = surveyQuestionnaireInfo.surveyFormsQuestion.find(
				(item) => item.questionId === formData.questionId
			);
			formData.isNew = false;
			let updatedSurveyFormsQuestion = [
				...surveyQuestionnaireInfo.surveyFormsQuestion,
				{ ...formData },
			];
			if (savedItems) {
				updatedSurveyFormsQuestion =
					surveyQuestionnaireInfo.surveyFormsQuestion.map((question) => {
						if (question.questionId === formData.questionId) {
							// assuming each question has a unique id
							return { ...formData }; // update the desired item
						}
						return question; // keep the other items as they are
					});
			}
			dispatch(
				updateSurveyQuestionnaire({
					...surveyQuestionnaireInfo,
					currentStep: QUE_JOURNEY_STEPS.STEP_QUESTIONS,
					surveyFormsQuestion: updatedSurveyFormsQuestion,
				})
			);
			updatLocalStateItmes(formData);
		}
	};
	return (
		<div className={classes.root}>
			<Loader isLoading={showLoader} />
			<Paper className={classes.flexPaper}>
				<div style={{ height: 'auto', paddingBottom: '90px' }}>
					<QuestionHeader />
					<QuestionSubHeader
						showEditIcon={true}
						handleBack={handleBack}
						backToDetailsPage={backToDetailsPage}
						activeStep={activeStep}
						width="102%"
					/>
					<QuestionsList
						onQueSave={saveAndEditQuestion}
						onQueClone={handleQueClone}
						deleteQue={handlerDeleteQuestion}
						items={items}
						onDragEnd={onDragEnd}
						onQueInputChange={onQueInputChange}
						onInstructionChange={onInstructionChange}
					/>

					<Box className={classes.actionBox}>
						<AddQuestionButton
							onClick={() => handleAddQuestion()}
							data-testid="add-question-button"
						>
							<Add className={classes.plusIcon} />
							{commonLabels.addNewQue}
						</AddQuestionButton>
					</Box>
				</div>
				<QuestionnaireFooter
					handleNext={saveAsProceed}
					handleSaveDraft={saveAsDraft}
					buttonEnable={disableSubmit}
					index={pageFooterIndex}
					data-testid="save-proceed-button"
				></QuestionnaireFooter>
			</Paper>
			<QuestionPopup open={popupOpen} onClose={handleClosePopupQue} />
			<GenericModalPopup
				openProp={open}
				msgBody={commonLabels.saveAsDraftText}
				mainMsg=""
				rightBtnText={commonLabels.ok}
				leftBtnText={commonLabels.no}
				isRightBtnVisible={true}
				isLeftBtnVisible={false}
				rightBtnHandler={handleClosePopup}
				leftBtnHandler={handleClosePopup}
			/>
		</div>
	);
};
export default CreateSurveyQuestions;
